import { useCallback, useState } from "react";
import { generatePath, Link } from "react-router";

import { cn } from "@/lib/chillnn/cn";
import { isDev } from "@/lib/chillnn/enviroment";
import type { SideMenuMobileQuery } from "@/lib/gql/__generated__/graphql";

import { externalLink } from "@/routes/_constants";
import { PATHS } from "@/routes/paths";

import { AuthSignOutButton } from "@/components/features/auth/sign-out/button";
import { Trial } from "@/components/features/side-menu/_shared/components";
import * as Icon from "@/components/features/side-menu/_shared/icon";

import { Button } from "@/components/ui/button/button";
import OpenInNew from "@/components/ui/icon/open-in-new";
import { Image } from "@/components/ui/image/image";
import { Label } from "@/components/ui/label/label";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

import { SIDE_MENU_MOBILE_ID } from "./_constants";
import { SideMenuItem } from "../_shared/components/side-menu-item";
import { SideMenuItemWrapper } from "../_shared/components/side-menu-item-wrapper";

type Props = {
	hotelID: string;
	sideMenuData: SideMenuMobileQuery;
	hotelApplicationData: {
		hotelApplicationStatus: {
			isHotelPolicyRegistered: boolean;
			isHotelRegistered: boolean;
			validDtEndBasicInitialSetUp: string | null;
		};
	};
};

export const SideMenuMobilePresenter = ({
	hotelID,
	sideMenuData,
	hotelApplicationData: {
		hotelApplicationStatus: { isHotelPolicyRegistered, isHotelRegistered, validDtEndBasicInitialSetUp },
	},
}: Props) => {
	const [open, setOpen] = useState(false);
	const handleOpen = useCallback(() => setOpen(true), []);
	const handleClose = useCallback(() => setOpen(false), []);
	const handleOpenChange = useCallback(
		(nextOpen: boolean) => {
			if (nextOpen) {
				handleOpen();
			} else {
				handleClose();
			}
		},
		[handleClose, handleOpen],
	);

	const newRequestReservationCount = sideMenuData.reservationRequestListByMonthCount.reservations.length;
	const isShowHPB = !!sideMenuData.hpbSettings;

	return (
		<div
			id={SIDE_MENU_MOBILE_ID}
			className={cn(
				"fixed top-0 z-50", //
				"h-12 w-full border-b border-b-zinc-100 bg-white px-5 print:hidden",
			)}
		>
			<div className="grid h-full grid-cols-[1fr_auto_1fr] items-center justify-between">
				{/* サイドメニューを開くトリガー */}
				<div className="inline-flex items-center">
					<Sheet open={open} onOpenChange={handleOpenChange}>
						<SheetTrigger asChild>
							<Button type="button" variant="no-outline" size="icon">
								<Icon.MenuIcon />
							</Button>
						</SheetTrigger>
						<SheetContent side="left" className="w-full overflow-y-auto bg-white">
							<div className="flex h-full flex-col justify-between">
								<div>
									{!!sideMenuData.hotel.name && (
										<div className="mx-2 mb-4 space-y-3 rounded border border-zinc-100 p-3">
											<p className="line-clamp-3 break-all text-sm">{sideMenuData.hotel.name}</p>
											<Trial validDtEndBasicInitialSetUp={validDtEndBasicInitialSetUp} />
										</div>
									)}

									<SideMenuItem
										href={generatePath(PATHS.HOTEL.TOP, { hotelID })}
										title={"ホーム"}
										icon={<Icon.HomeIcon />}
										callbackFunc={handleClose}
									/>

									{isHotelPolicyRegistered && (
										<SideMenuItemWrapper
											title={"インボイス制度"}
											childrenPaths={[
												generatePath(PATHS.HOTEL.INVOICE_SYSTEM.SETTINGS, { hotelID }),
											]}
											icon={<Icon.InvoiceSystemIcon />}
										>
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.INVOICE_SYSTEM.SETTINGS, { hotelID })}
												title="設定"
												callbackFunc={handleClose}
											/>
										</SideMenuItemWrapper>
									)}

									{isShowHPB && isHotelPolicyRegistered && (
										<SideMenuItem
											href={generatePath(PATHS.HOTEL.HPB.LIST, { hotelID })}
											title={"HP作成"}
											icon={<Icon.HomePageBuilderIcon />}
											callbackFunc={handleClose}
										/>
									)}

									{isHotelPolicyRegistered && (
										<SideMenuItemWrapper
											title={"デザイン"}
											useAmbiguousMatching
											childrenPaths={[
												generatePath(PATHS.HOTEL.RESERVATION_PAGE.BASE, { hotelID }),
												generatePath(PATHS.HOTEL.RESERVATION_PAGE.MAGAZINE.LIST.PLAN, {
													hotelID,
												}),
												generatePath(PATHS.HOTEL.RESERVATION_PAGE.MAGAZINE.LIST.HOTEL, {
													hotelID,
												}),
												generatePath(PATHS.HOTEL.RESERVATION_PAGE.MAGAZINE.LIST.ROOM, {
													hotelID,
												}),
												generatePath(PATHS.HOTEL.RESERVATION_PAGE.MAGAZINE.SETTING, {
													hotelID,
												}),
												generatePath(PATHS.HOTEL.RESERVATION_PAGE.SNACK.LIST, { hotelID }),
											]}
											icon={<Icon.DesignIcon />}
										>
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.RESERVATION_PAGE.BASE, { hotelID })}
												title="プレーン"
												callbackFunc={handleClose}
											/>
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.RESERVATION_PAGE.MAGAZINE.LIST.HOTEL, {
													hotelID,
												})}
												ambiguousPath={[`/${hotelID}/reservationPage/magazine`]}
												title={"マガジン"}
												callbackFunc={handleClose}
											/>
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.RESERVATION_PAGE.SNACK.LIST, {
													hotelID,
												})}
												ambiguousPath={[
													generatePath(PATHS.HOTEL.RESERVATION_PAGE.SNACK.LIST, { hotelID }),
												]}
												title={"スナック"}
												callbackFunc={handleClose}
											/>

											<div className="my-3 h-px w-full border-b border-zinc-200" />

											<SideMenuItem
												targetBlank
												href="https://chillnn.notion.site/25cfffe15c57802b8293f5e4de6921f2"
												title={
													<div className="flex items-center gap-x-2">
														<p>３つのデザインの違い</p>
														<OpenInNew className="size-[18px]" />
													</div>
												}
												callbackFunc={handleClose}
											/>
										</SideMenuItemWrapper>
									)}

									{isHotelPolicyRegistered && (
										<SideMenuItemWrapper
											title={"マーケティング"}
											useAmbiguousMatching
											childrenPaths={[
												generatePath(PATHS.HOTEL.MARKETING.CAMPAIGN.TOP, { hotelID }),
												generatePath(PATHS.HOTEL.MARKETING.VOUCHER.TOP, { hotelID }),
											]}
											icon={<Icon.MarketingIcon />}
										>
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.MARKETING.CAMPAIGN.TOP, { hotelID })}
												ambiguousPath={[
													generatePath(PATHS.HOTEL.MARKETING.CAMPAIGN.TOP, { hotelID }),
												]}
												title={"キャンペーン"}
												callbackFunc={handleClose}
											/>
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.MARKETING.VOUCHER.TOP, { hotelID })}
												ambiguousPath={[
													generatePath(PATHS.HOTEL.MARKETING.VOUCHER.TOP, { hotelID }),
												]}
												title={"宿泊券"}
												callbackFunc={handleClose}
											/>
										</SideMenuItemWrapper>
									)}

									{isHotelPolicyRegistered && (
										<SideMenuItemWrapper
											title={"顧客管理"}
											useAmbiguousMatching
											childrenPaths={[
												generatePath(PATHS.HOTEL.MANAGEMENT.RESERVATION.TOP, { hotelID }),
												generatePath(PATHS.HOTEL.MANAGEMENT.SALES.TOP, { hotelID }),
												generatePath(PATHS.HOTEL.MANAGEMENT.REFUND_REQUEST.TOP, { hotelID }),
												generatePath(PATHS.HOTEL.MANAGEMENT.EC_SALES.TOP, { hotelID }),
												generatePath(PATHS.HOTEL.MANAGEMENT.ACTIVITIES.OPTIONS, { hotelID }),
											]}
											icon={<Icon.CustomerIcon />}
										>
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.MANAGEMENT.RESERVATION.TOP, {
													hotelID,
												})}
												ambiguousPath={[
													generatePath(PATHS.HOTEL.MANAGEMENT.RESERVATION.TOP, {
														hotelID,
													}),
												]}
												title="予約"
												titleRightChildren={
													newRequestReservationCount > 0 ? (
														<Label
															text={newRequestReservationCount.toString()}
															variant="circle"
														/>
													) : undefined
												}
												callbackFunc={handleClose}
											/>
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.MANAGEMENT.EC_SALES.TOP, {
													hotelID,
												})}
												ambiguousPath={[
													generatePath(PATHS.HOTEL.MANAGEMENT.EC_SALES.TOP, { hotelID }),
												]}
												title={"EC販売"}
												callbackFunc={handleClose}
											/>
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.MANAGEMENT.SALES.TOP, { hotelID })}
												ambiguousPath={[generatePath(PATHS.HOTEL.MANAGEMENT.SALES.TOP, { hotelID })]}
												title={"収支"}
												callbackFunc={handleClose}
											/>
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.MANAGEMENT.REFUND_REQUEST.TOP, {
													hotelID,
												})}
												title={"返金"}
												callbackFunc={handleClose}
											/>
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.MANAGEMENT.ACTIVITIES.OPTIONS, {
													hotelID,
												})}
												title="オプション管理"
												callbackFunc={handleClose}
											/>
										</SideMenuItemWrapper>
									)}

									{isHotelPolicyRegistered && (
										<SideMenuItemWrapper
											title={"在庫管理"}
											childrenPaths={[
												generatePath(PATHS.HOTEL.ITEMS.PLAN.STOCK, { hotelID }),
												generatePath(PATHS.HOTEL.ITEMS.ROOM.STOCK, { hotelID }),
												generatePath(PATHS.HOTEL.ITEMS.OPTION.STOCK, { hotelID }),
											]}
											icon={<Icon.InventoryIcon />}
										>
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.ITEMS.PLAN.STOCK, { hotelID })}
												title={"プラン"}
												callbackFunc={handleClose}
											/>
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.ITEMS.ROOM.STOCK, { hotelID })}
												title={"部屋"}
												callbackFunc={handleClose}
											/>
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.ITEMS.OPTION.STOCK, { hotelID })}
												title={"オプション"}
												callbackFunc={handleClose}
											/>
										</SideMenuItemWrapper>
									)}

									{isHotelPolicyRegistered && (
										<SideMenuItemWrapper
											title={"料金管理"}
											childrenPaths={[
												generatePath(PATHS.HOTEL.ITEMS.PLAN.RATE_MATRIX, { hotelID }),
												generatePath(PATHS.HOTEL.ITEMS.ROOM.PRICE_RANK, { hotelID }),
											]}
											icon={<Icon.PriceCheckIcon />}
										>
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.ITEMS.PLAN.RATE_MATRIX, { hotelID })}
												title={"料金一覧"}
												titleRightChildren={<Label text="ベータ" variant="black" size="xs" />}
												callbackFunc={handleClose}
											/>
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.ITEMS.ROOM.PRICE_RANK, { hotelID })}
												title={"部屋料金"}
												callbackFunc={handleClose}
											/>
										</SideMenuItemWrapper>
									)}

									{isHotelPolicyRegistered && (
										<SideMenuItemWrapper
											title={"商品管理"}
											useAmbiguousMatching
											childrenPaths={[
												generatePath(PATHS.HOTEL.ITEMS.PLAN.TOP, { hotelID }),
												generatePath(PATHS.HOTEL.ITEMS.ROOM.TOP, { hotelID }),
												generatePath(PATHS.HOTEL.ITEMS.OPTION.TOP, { hotelID }),
												generatePath(PATHS.HOTEL.ITEMS.DISCOUNT.TOP, { hotelID }),
											]}
											excludePaths={[
												generatePath(PATHS.HOTEL.ITEMS.PLAN.STOCK, { hotelID }),
												generatePath(PATHS.HOTEL.ITEMS.ROOM.STOCK, { hotelID }),
												generatePath(PATHS.HOTEL.ITEMS.OPTION.STOCK, { hotelID }),
												generatePath(PATHS.HOTEL.ITEMS.ROOM.PRICE_RANK, { hotelID }),
												generatePath(PATHS.HOTEL.ITEMS.PLAN.RATE_MATRIX, { hotelID }),
											]}
											icon={<Icon.GoodsIcon />}
										>
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.ITEMS.PLAN.TOP, { hotelID })}
												ambiguousPath={[generatePath(PATHS.HOTEL.ITEMS.PLAN.TOP, { hotelID })]}
												excludePath={[
													generatePath(PATHS.HOTEL.ITEMS.PLAN.STOCK, { hotelID }),
													generatePath(PATHS.HOTEL.ITEMS.PLAN.RATE_MATRIX, { hotelID }),
												]}
												title={"プラン"}
												callbackFunc={handleClose}
											/>
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.ITEMS.ROOM.TOP, { hotelID })}
												ambiguousPath={[generatePath(PATHS.HOTEL.ITEMS.ROOM.TOP, { hotelID })]}
												excludePath={[
													generatePath(PATHS.HOTEL.ITEMS.ROOM.STOCK, { hotelID }),
													generatePath(PATHS.HOTEL.ITEMS.ROOM.PRICE_RANK, { hotelID }),
												]}
												title={"部屋"}
												callbackFunc={handleClose}
											/>
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.ITEMS.OPTION.TOP, { hotelID })}
												ambiguousPath={[
													generatePath(PATHS.HOTEL.ITEMS.OPTION.TOP, { hotelID }),
												]}
												excludePath={generatePath(PATHS.HOTEL.ITEMS.OPTION.STOCK, {
													hotelID,
												})}
												title={"オプション"}
												callbackFunc={handleClose}
											/>
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.ITEMS.DISCOUNT.TOP, { hotelID })}
												ambiguousPath={[
													generatePath(PATHS.HOTEL.ITEMS.DISCOUNT.TOP, { hotelID }),
												]}
												title="ディスカウント"
												callbackFunc={handleClose}
											/>
										</SideMenuItemWrapper>
									)}

									<SideMenuItemWrapper
										icon={<Icon.AccommodationIcon />}
										title={"施設設定"}
										useAmbiguousMatching
										childrenPaths={[
											generatePath(PATHS.HOTEL.HOTEL_SETTINGS.EDIT.DEFAULT, { hotelID }),
											generatePath(PATHS.HOTEL.HOTEL_SETTINGS.HOTEL_POLICY.TOP, { hotelID }),
											generatePath(PATHS.HOTEL.HOTEL_SETTINGS.EMAIL, { hotelID }),
											generatePath(PATHS.HOTEL.HOTEL_SETTINGS.MAIL_SETTINGS.TOP, { hotelID }),
										]}
									>
										<SideMenuItem
											href={generatePath(PATHS.HOTEL.HOTEL_SETTINGS.EDIT.DEFAULT, { hotelID })}
											ambiguousPath={[
												generatePath(PATHS.HOTEL.HOTEL_SETTINGS.EDIT.DEFAULT, { hotelID }),
											]}
											title={"施設情報"}
											callbackFunc={handleClose}
										/>
										{isHotelRegistered && (
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.HOTEL_SETTINGS.HOTEL_POLICY.TOP, {
													hotelID,
												})}
												ambiguousPath={[
													generatePath(PATHS.HOTEL.HOTEL_SETTINGS.HOTEL_POLICY.TOP, {
														hotelID,
													}),
												]}
												title={"ホテルポリシー"}
												callbackFunc={handleClose}
											/>
										)}
										{isHotelRegistered && (
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.HOTEL_SETTINGS.MAIL_SETTINGS.TOP, {
													hotelID,
												})}
												ambiguousPath={[
													generatePath(PATHS.HOTEL.HOTEL_SETTINGS.MAIL_SETTINGS.TOP, {
														hotelID,
													}),
												]}
												title={"基本メール設定"}
												callbackFunc={handleClose}
											/>
										)}
										{isHotelPolicyRegistered && (
											<SideMenuItem
												href={generatePath(PATHS.HOTEL.HOTEL_SETTINGS.EMAIL, { hotelID })}
												title="通知設定"
												callbackFunc={handleClose}
											/>
										)}
									</SideMenuItemWrapper>

									<SideMenuItemWrapper
										title={"設定"}
										useAmbiguousMatching
										childrenPaths={[
											generatePath(PATHS.SETTINGS.PAYMENT_CREDIT, { hotelID }),
											generatePath(PATHS.SETTINGS.PAYMENT_DEPOSIT_ACCOUNT, { hotelID }),
											generatePath(PATHS.SETTINGS.BILLING, { hotelID }),
											generatePath(PATHS.SETTINGS.SECURITY, { hotelID }),
										]}
										icon={<Icon.SettingsIcon />}
									>
										<SideMenuItem
											href={generatePath(PATHS.SETTINGS.PAYMENT_CREDIT, { hotelID })}
											title="お支払い情報"
											callbackFunc={handleClose}
										/>
										<SideMenuItem
											href={generatePath(PATHS.SETTINGS.PAYMENT_DEPOSIT_ACCOUNT, { hotelID })}
											title="入金先情報"
											callbackFunc={handleClose}
										/>
										<SideMenuItem
											href={generatePath(PATHS.SETTINGS.BILLING, { hotelID })}
											title={"料金プラン"}
											callbackFunc={handleClose}
										/>
										<SideMenuItem
											href={generatePath(PATHS.SETTINGS.SECURITY, { hotelID })}
											title="セキュリティ"
											callbackFunc={handleClose}
										/>
									</SideMenuItemWrapper>

									{isHotelPolicyRegistered && (
										<SideMenuItemWrapper
											icon={<Icon.CloudSyncIcon />}
											title={"外部接続設定"}
											useAmbiguousMatching
											childrenPaths={[
												generatePath(PATHS.SETTINGS.SITE_CONTROLLER, { hotelID }),
												generatePath(
													PATHS.SETTINGS.EXTERNAL_CONNECTION.GOOGLE_FREE_BOOKING_LINKS
														.DEFAULT,
													{ hotelID },
												),
												generatePath(PATHS.SETTINGS.EXTERNAL_CONNECTION.SITE_EMBED, { hotelID }),
											]}
										>
											<SideMenuItem
												href={generatePath(PATHS.SETTINGS.SITE_CONTROLLER, { hotelID })}
												title={"サイトコントローラー"}
												callbackFunc={handleClose}
											/>
											<SideMenuItem
												href={generatePath(
													PATHS.SETTINGS.EXTERNAL_CONNECTION.GOOGLE_FREE_BOOKING_LINKS
														.DEFAULT,
													{ hotelID },
												)}
												title={"Google 無料予約リンク"}
												callbackFunc={handleClose}
											/>
											<SideMenuItem
												href={generatePath(PATHS.SETTINGS.EXTERNAL_CONNECTION.SITE_EMBED, {
													hotelID,
												})}
												title={"サイト埋め込み"}
												callbackFunc={handleClose}
											/>
										</SideMenuItemWrapper>
									)}

									<div className="my-4 border-b border-zinc-200" />

									<SideMenuItem
										href={externalLink.faqSiteUrl(hotelID)}
										title="困ったときは"
										targetBlank
										icon={<Icon.SupportIcon className="h-[22px] w-[22px]" />}
										callbackFunc={handleClose}
									/>
									<SideMenuItem
										href="https://typebot.co/chillnn-reservation-test-y04lb9k"
										title="予約できないときは"
										targetBlank
										icon={<Icon.DiagnosticIcon className="h-[22px] w-[22px]" />}
										callbackFunc={handleClose}
									/>
								</div>

								<div className="space-y-6">
									<div>
										<SideMenuItem
											href={PATHS.TOP}
											title={"ホテル一覧"}
											callbackFunc={handleClose}
										/>
										<SideMenuItem
											href={PATHS.SETUP}
											title={"ホテル追加"}
											callbackFunc={handleClose}
										/>
										<SideMenuItem
											href={externalLink.chillnnNotion}
											targetBlank
											title={"最新情報"}
											callbackFunc={handleClose}
										/>
										<SideMenuItem
											href={generatePath(PATHS.POLICY.SERVICE_POLICY)}
											targetBlank
											title={"利用規約"}
											callbackFunc={handleClose}
										/>
										<SideMenuItem
											href={generatePath(PATHS.CANCELLATION, { hotelID })}
											title={"解約する"}
											callbackFunc={handleClose}
										/>
									</div>
									<div role="button" onClick={handleClose}>
										<AuthSignOutButton className="w-full px-2" variant="outline" />
									</div>
								</div>
							</div>
						</SheetContent>
					</Sheet>
				</div>

				{/* CHILLNNロゴ */}
				<Link to={"/"} viewTransition>
					<Image src="/chillnn-logo-black.png" className="h-full w-14" />
				</Link>

				{isDev && (
					<Label
						className="w-fit cursor-help select-none place-self-end self-center"
						variant="pink"
						size="default"
						text="開発環境"
					/>
				)}
			</div>
		</div>
	);
};

SideMenuMobilePresenter.displayName = "SideMenuMobilePresenter";
